import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { Loader } from '@/components/loader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import UserAPI from '@/api/user.api';
import { AlertCircle, GitMerge } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const MERGE_USERS_SCHEMA = z
  .object({
    primaryEmail: z.string().email({ message: 'Invalid email format' }).toLowerCase(),
    secondaryEmail: z.string().email({ message: 'Invalid email format' }).toLowerCase(),
  })
  .refine(data => data.primaryEmail !== data.secondaryEmail, {
    message: 'Primary email and secondary email cannot be the same',
    path: ['secondaryEmail'],
  });

export default function MergeUsersForm() {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);
  const [error, setError] = useState(null);
  const { toast } = useToast();

  const form = useForm({
    resolver: zodResolver(MERGE_USERS_SCHEMA),
    defaultValues: {
      primaryEmail: '',
      secondaryEmail: '',
    },
  });

  const onSubmit = async formData => {
    setLoading(true);
    setError(null);
    setMergeResult(null);

    try {
      const { data, error: apiError } = await UserAPI.mergeUsers({
        primaryEmail: formData.primaryEmail.toLowerCase(),
        secondaryEmail: formData.secondaryEmail.toLowerCase(),
      });

      setLoading(false);

      if (apiError) {
        setError(apiError.message || 'An error occurred during the merge');
        toast({
          status: 'error',
          title: 'Merge Failed',
          description: apiError.message || 'No changes were made',
          variant: 'destructive',
        });
        return;
      }

      setMergeResult(data);
      toast({
        status: 'success',
        title: 'Users Merged Successfully',
        description: `${formData.secondaryEmail} has been merged into ${formData.primaryEmail}`,
      });

      form.reset();
    } catch (err) {
      setLoading(false);
      setError(err.message || 'An unexpected error occurred');
      toast({
        status: 'error',
        title: 'Merge Failed',
        description: 'No changes were made',
        variant: 'destructive',
      });
    }
  };

  const handleClose = isOpen => {
    if (!isOpen) {
      setMergeResult(null);
      setError(null);
      form.reset();
    }
    setOpen(isOpen);
  };

  const handleMergeAnother = () => {
    setMergeResult(null);
    setError(null);
    form.reset();
  };

  const renderMergeStats = () => {
    if (!mergeResult || !mergeResult.migrationStats) return null;

    const stats = Object.entries(mergeResult.migrationStats);

    return (
      <div className="space-y-4 mt-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Merged User Information</CardTitle>
            <CardDescription>Email: {mergeResult.user?.email || 'N/A'}</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Migration Statistics</CardTitle>
            <CardDescription>Changes made by organization</CardDescription>
          </CardHeader>
          <CardContent>
            {stats.length === 0 ? (
              <p className="text-muted-foreground">No data migrated</p>
            ) : (
              <div className="space-y-4">
                {stats.map(([orgId, orgStats]) => (
                  <Card key={orgId} className="border-2">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        {orgStats.organizationName || orgId}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-2">
                        {orgStats.rotationTimeSlots > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">
                              Rotation Time Slots:
                            </span>
                            <Badge variant="secondary">{orgStats.rotationTimeSlots}</Badge>
                          </div>
                        )}
                        {orgStats.communications > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Communications:</span>
                            <Badge variant="secondary">{orgStats.communications}</Badge>
                          </div>
                        )}
                        {orgStats.communicationRecipients > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">
                              Communication Recipients:
                            </span>
                            <Badge variant="secondary">{orgStats.communicationRecipients}</Badge>
                          </div>
                        )}
                        {orgStats.userQualityMetrics > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">
                              User Quality Metrics:
                            </span>
                            <Badge variant="secondary">{orgStats.userQualityMetrics}</Badge>
                          </div>
                        )}
                        {orgStats.qualityMetricRecords > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">
                              Quality Metric Records:
                            </span>
                            <Badge variant="secondary">{orgStats.qualityMetricRecords}</Badge>
                          </div>
                        )}
                        {orgStats.qualityMetrics > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Quality Metrics:</span>
                            <Badge variant="secondary">{orgStats.qualityMetrics}</Badge>
                          </div>
                        )}
                        {orgStats.favorites > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Favorites:</span>
                            <Badge variant="secondary">{orgStats.favorites}</Badge>
                          </div>
                        )}
                        {orgStats.groups > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Groups:</span>
                            <Badge variant="secondary">{orgStats.groups}</Badge>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button className="ml-6 mb-3" variant="outline">
          <GitMerge className="mr-2 h-4 w-4" />
          Merge Users
        </Button>
      </DialogTrigger>
      <DialogContent className="min-w-fit overflow-y-scroll max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Merge Users</DialogTitle>
          <DialogDescription>
            Merge one user account into another. All data from the secondary user will be
            transferred to the primary user.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {error}
              <br />
              <strong>No changes have been made.</strong> Both users remain unchanged.
            </AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <Loader />
            <span className="ml-4">Processing merge...</span>
          </div>
        ) : mergeResult ? (
          renderMergeStats()
        ) : (
          <div className="grid gap-4 py-4">
            <Form {...form}>
              <form
                id="merge-users"
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6 w-full">
                <FormField
                  control={form.control}
                  name="primaryEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">Primary User Email</FormLabel>
                      <FormControl>
                        <Input placeholder="primary@example.com" {...field} disabled={loading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="secondaryEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">User to Merge Email</FormLabel>
                      <FormControl>
                        <Input placeholder="merge@example.com" {...field} disabled={loading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Warning</AlertTitle>
                  <AlertDescription>
                    This action will transfer all data from the secondary user into the primary
                    user. The secondary user account will be deleted. This action cannot be undone.
                  </AlertDescription>
                </Alert>

                <DialogFooter>
                  <Button type="submit" form="merge-users" disabled={loading}>
                    {loading ? 'Merging...' : 'Merge Users'}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </div>
        )}

        {mergeResult && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleMergeAnother}>
              <GitMerge className="mr-2 h-4 w-4" />
              Merge Another
            </Button>
            <Button onClick={() => handleClose(false)}>Close</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
